import { useState, useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

interface ObjectionItem {
  objection: string;
  answer: string;
  detail: string;
  image?: string;
}

const objectionsData: ObjectionItem[] = [
  {
    objection: '„Wir haben genug Kunden."',
    answer: 'Heute vielleicht. Aber wer morgen nicht gefunden wird, verliert.',
    detail: 'Sichtbarkeit ist kein Luxus – sondern Voraussetzung für planbare Anfragen.',
  },
  {
    objection: '„Unsere alte Website reicht noch."',
    answer: 'Für gestern vielleicht. Nicht für die Erwartungen Ihrer nächsten Kunden.',
    detail: 'Eine veraltete Website kostet Vertrauen, bevor überhaupt Kontakt entsteht.',
  },
  {
    objection: '„Wir schalten lieber Anzeigen."',
    answer: 'Anzeigen bringen Besucher. Ihre Website entscheidet den Rest.',
    detail: 'Ohne starke Website verpufft selbst guter Traffic deutlich schneller.',
  },
  {
    objection: '„Empfehlungen bringen uns genug."',
    answer: 'Empfehlungen enden trotzdem auf Ihrer Website.',
    detail: 'Dort entscheidet sich, ob aus Interesse echtes Vertrauen wird.',
  },
  {
    objection: '„Brauchen wir nicht."',
    answer: 'Ihre Konkurrenz sagt das nicht. Sie baut gerade aus.',
    detail: 'Wer digital nicht überzeugt, verliert leise gegen sichtbarere Anbieter.',
  },
  {
    objection: '„Ich kann das selbst."',
    answer: 'Stimmt. Noch nie war es so einfach, eine Website zu bauen.\nAber genau deshalb sehen viele auch gleich aus.',
    detail: 'Selbstgemacht heißt nicht selbstbewusst. Profis erkennen den Unterschied.',
    image: 'https://readdy.ai/api/search-image?query=a%20cute%20fluffy%20tabby%20cat%20sitting%20at%20a%20modern%20laptop%20computer%20on%20a%20desk%2C%20paws%20on%20keyboard%2C%20looking%20focused%20at%20the%20screen%2C%20cozy%20warm%20light%2C%20minimal%20clean%20white%20background%2C%20professional%20product%20photography%20style&width=320&height=220&seq=cat-laptop-objection-6&orientation=landscape',
  },
];

const getResponse = (input: string): string => {
  const text = input.toLowerCase();
  if (text.includes('praxis') || text.includes('arzt') || text.includes('therapeut'))
    return '77 % der Patienten suchen ihren Arzt online, bevor sie anrufen.';
  if (text.includes('handwerker') || text.includes('betrieb'))
    return '70 % der Handwerksbetriebe haben keine mobiloptimierte Website. Die Lücke ist Ihre Chance.';
  if (text.includes('kanzlei') || text.includes('anwalt') || text.includes('steuerberater'))
    return 'Das erste Urteil über eine Kanzlei fällt online – nicht im Gespräch.';
  if (text.includes('wordpress') || text.includes('wix') || text.includes('baukasten'))
    return 'Baukästen ranken schlechter, laden langsamer, sehen ähnlicher aus.';
  if (text.includes('bewertungen') || text.includes('google maps'))
    return '88 % der Nutzer vertrauen Online-Bewertungen wie persönlichen Empfehlungen – wenn die Website dazu passt.';
  if (text.includes('seo') || text.includes('gefunden'))
    return 'Seite 2 bei Google hat eine Klickrate von unter 1 %.';
  if (text.includes('konkurrenz') || text.includes('mitbewerber'))
    return '81 % der Käufer recherchieren online, bevor sie eine Entscheidung treffen.';
  if (text.includes('selbst') || text.includes('mache ich'))
    return 'Stimmt. Aber 38 % der Nutzer verlassen eine Website, wenn das Layout unattraktiv ist.';
  if (text.includes('klein') || text.includes('bin nur'))
    return '46 % aller Google-Suchen haben lokalen Bezug.';
  if (text.includes('später') || text.includes('irgendwann'))
    return 'Jeder Monat ohne gute Website ist ein Monat, in dem jemand anderes gefunden wird.';
  if (text.includes('vertrauen') || text.includes('seriös'))
    return '75 % der Nutzer schließen von der Website auf die Qualität des Unternehmens.';
  if (text.includes('schnell') || text.includes('langsam') || text.includes('lädt') || text.includes('ladezeit'))
    return '53 % der mobilen Nutzer verlassen eine Seite nach 3 Sekunden Ladezeit.';
  if (text.includes('mobil') || text.includes('handy') || text.includes('smartphone'))
    return '63 % aller Websuchen kommen vom Smartphone.';
  if (text.includes('design') || text.includes('schön'))
    return 'Design ist nicht Dekoration. Es ist das erste Qualitätsurteil Ihrer Kunden.';
  if (text.includes('texte') || text.includes('content') || text.includes('text'))
    return 'Nutzer lesen nicht – sie scannen. Wer das ignoriert, verliert sie nach 8 Sekunden.';
  if (text.includes('support') || text.includes('betreuung') || text.includes('pflege'))
    return 'Eine Website ohne Pflege verliert pro Jahr durchschnittlich 20 % ihrer Sichtbarkeit.';
  if (text.includes('kunden') || text.includes('aufträge') || text.includes('auftrag'))
    return '97 % der Menschen suchen lokale Unternehmen zuerst online. Wer sie nicht findet, fragt wen anderen.';
  if (text.includes('empfehlung') || text.includes('mundpropaganda') || text.includes('weiterempfehlung'))
    return 'Empfehlungen googeln Ihr Unternehmen, bevor sie anrufen. Was finden die dann?';
  if (text.includes('anzeigen') || text.includes('google ads') || text.includes('werbung') || text.includes('social media'))
    return 'Anzeigen enden, wenn das Budget endet. Eine gute Website arbeitet weiter.';
  if (text.includes('teuer') || text.includes('kostet') || text.includes('geld') || text.includes('budget') || text.includes('preis'))
    return 'Eine schwache Website kostet nichts. Und bringt auch nichts.';
  if (text.includes('zeit') || text.includes('keine zeit') || text.includes('beschäftigt'))
    return 'Eine Website arbeitet 24/7. Sie müssen das nicht.';
  if (text.includes('alt') || text.includes('reicht') || text.includes('funktioniert'))
    return '75 % der Nutzer beurteilen die Glaubwürdigkeit eines Unternehmens am Design.';
  if (text.includes('brauchen') || text.includes('nicht notwendig') || text.includes('unnötig'))
    return 'Ihr Mitbewerber hat gerade eine neue.';
  return 'Jeder Einwand klingt vernünftig – bis die nächste Anfrage beim Mitbewerber landet. Eine starke Website ist Ihre wichtigste Verkäuferin.';
};

export default function Objections() {
  const { theme } = useTheme();
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const [userInput, setUserInput] = useState('');
  const [response, setResponse] = useState('');
  const [isAnswering, setIsAnswering] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const goTo = (index: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(index);
      setAnimating(false);
    }, 220);
  };

  const prev = () => goTo((current - 1 + objectionsData.length) % objectionsData.length);
  const next = () => goTo((current + 1) % objectionsData.length);

  const handleSubmit = () => {
    if (!userInput.trim()) return;
    setIsAnswering(true);
    setHasAnswered(false);
    setResponse('');
    setTimeout(() => {
      setResponse(getResponse(userInput));
      setIsAnswering(false);
      setHasAnswered(true);
    }, 850);
  };

  const handleReset = () => {
    setUserInput('');
    setResponse('');
    setHasAnswered(false);
    setIsAnswering(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const item = objectionsData[current];

  return (
    <section
      ref={sectionRef}
      className="py-12 sm:py-20 md:py-28 px-4"
      style={{ backgroundColor: theme.colors.background, fontFamily: theme.fonts.body }}
    >
      <div className="max-w-5xl mx-auto">

        {/* Label */}
        <div
          className="mb-10"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(18px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <span
            className="text-xs tracking-[0.35em] uppercase font-semibold"
            style={{ color: theme.colors.accent }}
          >
            Die häufigsten Einwände
          </span>
        </div>

        {/* Panel */}
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.1s',
            backgroundColor: '#0A0A0A',
          }}
        >
          {/* Main content */}
          <div
            className="grid grid-cols-1 md:grid-cols-2"
            style={{
              opacity: animating ? 0 : 1,
              transform: animating ? 'translateY(8px)' : 'translateY(0)',
              transition: 'opacity 0.22s ease, transform 0.22s ease',
            }}
          >
            {/* Left – Objection */}
            <div
              className="p-5 sm:p-10 md:p-14 flex flex-col justify-center overflow-hidden"
              style={{
                borderRight: '1px solid rgba(255,255,255,0.06)',
                minHeight: '200px',
              }}
            >
              <p
                className="text-xs font-bold tracking-[0.25em] uppercase mb-4 sm:mb-5"
                style={{ color: 'rgba(255,255,255,0.25)' }}
              >
                Einwand
              </p>
              <p
                className="text-xl sm:text-2xl md:text-3xl font-bold italic leading-snug text-white"
                style={{ fontFamily: theme.fonts.heading, letterSpacing: '-0.02em' }}
              >
                {item.objection}
              </p>
              {item.image && (
                <div className="mt-5 flex-1 overflow-hidden rounded-lg" style={{ maxHeight: '100px' }}>
                  <img
                    src={item.image}
                    alt="Katze am Laptop"
                    className="w-auto h-full object-cover object-top rounded-lg"
                    style={{ maxWidth: '180px' }}
                  />
                </div>
              )}
            </div>

            {/* Right – Answer */}
            <div
              className="p-5 sm:p-10 md:p-14 flex flex-col justify-center overflow-hidden"
              style={{ minHeight: '180px' }}
            >
              <p
                className="text-xs font-bold tracking-[0.25em] uppercase mb-4 sm:mb-5"
                style={{ color: theme.colors.accent }}
              >
                Antwort
              </p>
              <p
                className="text-lg sm:text-xl md:text-2xl font-bold leading-snug"
                style={{
                  fontFamily: theme.fonts.heading,
                  letterSpacing: '-0.02em',
                  background: `linear-gradient(135deg, #fff 0%, ${theme.colors.secondary} 50%, ${theme.colors.accent} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  whiteSpace: 'pre-line',
                }}
              >
                {item.answer}
              </p>
            </div>
          </div>

          {/* Footer bar */}
          <div
            className="flex flex-wrap items-center justify-between gap-3 px-5 sm:px-10 md:px-14 py-4 sm:py-5"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
          >
            <p
              className="text-xs sm:text-sm leading-relaxed flex-1 min-w-0"
              style={{
                color: 'rgba(255,255,255,0.40)',
                opacity: animating ? 0 : 1,
                transition: 'opacity 0.22s ease',
              }}
            >
              {item.detail}
            </p>
            <div className="flex items-center gap-3 sm:gap-4 shrink-0 sm:ml-8">
              <span className="text-xs font-medium tabular-nums" style={{ color: 'rgba(255,255,255,0.25)' }}>
                {current + 1} / {objectionsData.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={prev}
                  className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 hover:opacity-70"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}
                  aria-label="Vorheriger Einwand"
                >
                  <i className="ri-arrow-left-s-line text-sm"></i>
                </button>
                <button
                  onClick={next}
                  className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 hover:opacity-70"
                  style={{
                    background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
                    color: '#fff',
                  }}
                  aria-label="Nächster Einwand"
                >
                  <i className="ri-arrow-right-s-line text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive objection tool */}
        <div
          className="mt-4 rounded-2xl overflow-hidden"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.25s',
            backgroundColor: '#0A0A0A',
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left – prompt + input */}
            <div
              className="p-5 sm:p-10 md:p-14 flex flex-col justify-center"
              style={{ borderRight: '1px solid rgba(255,255,255,0.06)' }}
            >
              <p
                className="text-xs font-bold tracking-[0.25em] uppercase mb-5 sm:mb-6"
                style={{ color: 'rgba(255,255,255,0.25)' }}
              >
                Ihr Einwand
              </p>
              <p
                className="text-base font-semibold leading-snug text-white/70 mb-6"
                style={{ fontFamily: theme.fonts.heading }}
              >
                Formulieren Sie Ihren Einwand — und sehen Sie ihn aus einer anderen Perspektive.
              </p>
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={userInput}
                  onChange={(e) => {
                    setUserInput(e.target.value);
                    if (hasAnswered) { setHasAnswered(false); setResponse(''); }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                  placeholder={'z.B. \u201EWir haben genug Kunden.\u201C'}
                  className="flex-1 text-sm px-4 py-2.5 outline-none"
                  style={{
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.10)',
                    color: 'rgba(255,255,255,0.85)',
                    fontFamily: theme.fonts.body,
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = theme.colors.accent; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; }}
                />
                <button
                  onClick={handleSubmit}
                  disabled={!userInput.trim() || isAnswering}
                  className="whitespace-nowrap px-5 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer flex items-center gap-1.5"
                  style={{
                    borderRadius: '8px',
                    background: !userInput.trim() || isAnswering
                      ? 'rgba(255,255,255,0.08)'
                      : `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
                    color: !userInput.trim() || isAnswering ? 'rgba(255,255,255,0.25)' : '#fff',
                    cursor: !userInput.trim() || isAnswering ? 'not-allowed' : 'pointer',
                    border: 'none',
                  }}
                >
                  {isAnswering ? (
                    <i className="ri-loader-4-line" style={{ animation: 'objSpin 0.8s linear infinite' }} />
                  ) : (
                    <>Antwort <i className="ri-arrow-right-line"></i></>
                  )}
                </button>
              </div>
            </div>

            {/* Right – response */}
            <div className="p-5 sm:p-10 md:p-14 flex flex-col justify-center min-h-[140px] sm:min-h-[160px]">
              {!hasAnswered && !isAnswering && (
                <p className="text-sm italic" style={{ color: 'rgba(255,255,255,0.18)' }}>
                  Ihre Antwort erscheint hier.
                </p>
              )}
              {isAnswering && (
                <div className="flex items-center gap-2.5">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        backgroundColor: theme.colors.accent,
                        animation: `objBounce 1.1s ease-in-out ${i * 0.18}s infinite`,
                      }}
                    />
                  ))}
                  <span className="text-xs ml-1" style={{ color: 'rgba(255,255,255,0.30)' }}>
                    Argument wird formuliert…
                  </span>
                </div>
              )}
              {hasAnswered && response && (
                <div style={{ animation: 'objFadeUp 0.5s ease forwards' }}>
                  <p
                    className="text-xl md:text-2xl font-bold leading-snug mb-5"
                    style={{
                      fontFamily: theme.fonts.heading,
                      letterSpacing: '-0.02em',
                      background: `linear-gradient(135deg, #fff 0%, ${theme.colors.secondary} 50%, ${theme.colors.accent} 100%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {response}
                  </p>
                  <button
                    onClick={handleReset}
                    className="text-xs tracking-wider uppercase font-semibold cursor-pointer flex items-center gap-2 transition-opacity duration-200 hover:opacity-70 whitespace-nowrap"
                    style={{ color: theme.colors.accent, background: 'none', border: 'none', padding: 0 }}
                  >
                    <i className="ri-refresh-line" />
                    Neuen Einwand eingeben
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      <style>{`
        @keyframes objSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes objBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
          30% { transform: translateY(-6px); opacity: 1; }
        }
        @keyframes objFadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
